import { ResolvedValueResult } from 'generated/mos/linkedfieldresolver'
import { filterLinkableAttributes, getResolvedValue, LinkableValue } from 'helpers/linkables'
import { Language } from 'generated/mos/i18n'
import { FormBag } from 'react-formage'

type ValueOf<T> = T[keyof T];

export type TranslationFormBags<TFormValues, TLanguageCode extends string> = {
  [key in TLanguageCode]: FormBag<TFormValues>;
}

type SchemaItem<TEntity> = {
  readonly read: (value: any, language?: Language.Entity) => LinkableValue;
  readonly write: (
    value: LinkableValue,
    // pass existing values so translatables can be set per language.
    existingValue?: ValueOf<TEntity>,
    language?: Language.Entity,
  ) => any;
  readonly validate?: {
    readonly required?: {
      readonly enabled: boolean;
      readonly message: string;
    };
  };
}

export type Schema<TEntity> = { readonly [key in keyof TEntity | string]: SchemaItem<TEntity> };

export const convertEntityToFormValues = <TEntity>(
  entity: Partial<TEntity>,
  schema: Schema<TEntity>,
  resolvedValues?: ReadonlyArray<ResolvedValueResult.Entity>,
  language?: Language.Entity
): { [key: string]: any } | {} =>
  Object.entries(entity)
    // ignore general entity and linkable attributes.
    .filter(attribute => {
      const key = attribute[0]
      return filterLinkableAttributes(key)
    })
    // extract the values.
    .reduce((accumulator, [key, rawValue]) => {

    // FIXME: This casting was done to work around the string coversion of input fields from formage
    // Needs to be fixed when we get rid of formage to accept number as an input
      const value = typeof rawValue === "number" ?
        rawValue as any as string :
        ((rawValue as any) && ((rawValue as any).hex !== undefined) ? (rawValue as any).hex as string : rawValue as any as LinkableValue)

      return {
        ...accumulator,
        // use schema to correctly extract the value, or fallback to directly using the value.
        [key]: getResolvedValue(schema[key] && value ? schema[key].read(value, language) : value, resolvedValues)
      }
    }, {})

export const writeUsingSchema = <TEntity>(
  schema: Schema<TEntity>,
  key: keyof TEntity,
  value: LinkableValue,
  existingValue?: ValueOf<TEntity>,
  language?: Language.Entity,
) => value ? schema[key] ? schema[key].write(value, existingValue, language) : value : undefined

export const translateValues = <TEntity, TFormValues, TLanguageCode extends string>(
  schema: Schema<TEntity> ,
  entity: TEntity,
  languages: ReadonlyArray<Language.Entity>,
  formBags: TranslationFormBags<TFormValues, TLanguageCode>
) =>
  (key: keyof TEntity) =>
    languages.reduce((acc: any[], curr: Language.Entity) => {
      const languageCode = curr.languageCode as keyof TranslationFormBags<TFormValues, TLanguageCode>
      const { values } = formBags[languageCode]
      const formbagKey = key as any as keyof FormBag<TFormValues>['values']
      const write = schema[key].write(values[formbagKey] as any as LinkableValue, entity[key], curr)
      if(write === undefined) return acc
      const value = write.translatedValues.find((value: any) => value.language && value.language.languageCode === languageCode)
      return [...acc, value]
    }, [])
